<script setup>
  import { ref, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from '../../services/ApiService.js';
  import ImprintsFilters from './ImprintsFilters.vue';
  import ImprintsTable from './ImprintsTable.vue';
  import AutorsTable from './AutorsTable.vue';

  const { t } = useI18n();
  const i18nScope = ref('components.imprints_list');
  const imprintsTable = ref(null);
  const territories = ref([]);
  const showAuthorsTable = ref(false);
  const selectedImprint = ref(null);
  
  const props = defineProps({
    audiences: {
      type: Object,
      required: true
    },
    languages: {
      type: Object,
      required: true
    }
  });

  const filterData = ref({
    searchQuery: '',
    selectedAudience: '',
    selectedLanguage: '',
    selectedTerritory: '',
    showOnlyFollowed: false
  });

  const handleFilterChange = () => {
    if (imprintsTable.value) {
      imprintsTable.value.clearImprints();
      setTimeout(() => {
        imprintsTable.value.fetchImprints();
      }, 300);
    }
  };

  const handleSearch = (query) => {
    filterData.value.searchQuery = query;
    handleFilterChange();
  };

  const handleAudienceChange = (audienceId) => {
    filterData.value.selectedAudience = audienceId;
    handleFilterChange();
  };

  const handleTerritoryChange = (territoryId) => {
    filterData.value.selectedTerritory = territoryId;
    handleFilterChange();
  };

  const handleLanguageChange = (languageId) => {
    filterData.value.selectedLanguage = languageId;
    handleFilterChange();
  };

  const handleFollowedChange = (showOnlyFollowed) => {
    filterData.value.showOnlyFollowed = showOnlyFollowed;
    handleFilterChange();
  };

  const handleViewAuthors = (imprint) => {
    selectedImprint.value = imprint;
    showAuthorsTable.value = true;
  };

  const handleBackToImprints = () => {
    showAuthorsTable.value = false;
    selectedImprint.value = null;
  };

  const fetchTerritories = async () => {
    try {
      const response = await axios.get('/api/imprints/territories.json');
      territories.value = response.data;
    } catch (error) {
      console.error('Error fetching territories:', error);
      territories.value = [];
    }
  };

  onMounted(() => {
    fetchTerritories();
  });
</script>

<template>
  <div class="imprints-container">
    <div class="justify-content-start">
      <ImprintsFilters
        v-if="!showAuthorsTable"
        v-model:searchQuery="filterData.searchQuery"
        v-model:selectedAudience="filterData.selectedAudience"
        v-model:selectedLanguage="filterData.selectedLanguage"
        v-model:selectedTerritory="filterData.selectedTerritory"
        v-model:showOnlyFollowed="filterData.showOnlyFollowed"
        :audiences="props.audiences"
        :territories="territories"
        :languages="props.languages"
        @filter-change="handleFilterChange"
        @search="handleSearch"
        @audience-change="handleAudienceChange"
        @territory-change="handleTerritoryChange"
        @language-change="handleLanguageChange"
        @followed-change="handleFollowedChange"
      />

      <hr v-if="!showAuthorsTable">

      <!-- Imprints Table -->
      <ImprintsTable
        v-if="!showAuthorsTable"
        ref="imprintsTable"
        :searchQuery="filterData.searchQuery"
        :selectedAudience="filterData.selectedAudience"
        :selectedLanguage="filterData.selectedLanguage"
        :selectedTerritory="filterData.selectedTerritory"
        :showOnlyFollowed="filterData.showOnlyFollowed"
        class="my-4"
        @view-authors="handleViewAuthors"
      />

      <!-- Authors Table -->
      <AutorsTable
        v-if="showAuthorsTable && selectedImprint"
        :imprint-id="selectedImprint.id"
        :imprint-name="selectedImprint.name"
        class="my-4"
        @back="handleBackToImprints"
      />
    </div>
  </div>
</template>

<style lang="scss">
  @import '../../../../node_modules/bootstrap/scss/bootstrap.scss';
  @import '../../../../node_modules/bootstrap-vue-next/dist/bootstrap-vue-next';

  .imprints-container {
    padding: 20px 0;
  }
</style>

<i18n src="../../locales/en.json" global></i18n>
<i18n src="../../locales/fr.json" global></i18n>
<i18n src="../../locales/es.json" global></i18n>
<i18n src="../../locales/it.json" global></i18n>
<i18n src="../../locales/pt.json" global></i18n>
